/**
 * BRAND TOKENS — a client-facing surface carries the PRACTICE's brand, not ours. A tenant
 * supplies two or three colours plus a logo (`tenant_config.subject_theme`); everything else a
 * page needs is DERIVED here so the same components re-skin for another practice by changing
 * the inputs alone. Two surfaces wear it, from this one derivation:
 *
 * THE CLIENT FORMS (`apps/subject-surface`, through `deriveBrandTokens`):
 *
 *   --brand-primary          the practice's main colour: focus ring, current step, progress bar
 *   --brand-primary-text     a text-safe darkening of primary (links, primary button fill)
 *   --brand-secondary        the supporting colour: completed steps, selected choice cards
 *   --brand-secondary-text   text-safe darkening of secondary (selected radio / tick fill)
 *   --brand-accent           the DONE-STATE tint, ONE job — the found-record card, the picked
 *                            candidate and the submitted panel. Defaults to the platform SUCCESS
 *                            background (`var(--feedback-success-bg)`, passed through, never
 *                            copied) so a confirmed object reads confirmed; a tenant
 *                            `subject_theme.accent` still overrides it
 *   --surface-page           page ground, a near-neutral biased toward the primary hue
 *   --surface-card           cards and inputs, one step lighter than the page
 *   --text-primary           headings, labels, answers: near-black with the same hue bias
 *   --text-secondary         help text, captions, steps not current
 *   --border                 text-secondary at low opacity
 *
 * THE CLIENT PORTAL (`apps/client-portal`, through `derivePortalTokens`): the same inputs,
 * mapped onto the `--ard-*` / `--btn-*` tokens the portal's stylesheets already read, plus the
 * three `--portal-nav-*` variables its side nav reads. See `derivePortalTokens` for the map.
 *
 * Semantic states (--success / --warning / --error, the status and feedback families) are NOT
 * brand colours and come from the platform token file, constant across practices. Contrast
 * targets: 4.5:1 for text, 3:1 for UI edges (WCAG 2.1 AA); every derived text colour is
 * darkened until it clears the bar on the surfaces it sits on. No colour literal lives in this
 * file: every value is computed from the inputs, except the accent's default, which is a
 * `var()` PASSTHROUGH to the platform token file.
 *
 * The accent default is a string, not an `Rgb`, so nothing here can contrast-check text against
 * it. The text that sits on it is fixed in the forms' CSS instead
 * (`apps/subject-surface/src/components/styles.css`, the scoped done-state rules): the eyebrow
 * reads `--feedback-success` and the secondary text reads `--text-primary`, both of which clear
 * AA on the success background (`apps/subject-surface/src/theme/brandTokens.test.ts`).
 */
export function parseHex(value) {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
    if (!m)
        return null;
    let h = m[1];
    if (h.length === 3)
        h = h.split("").map((c) => c + c).join("");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
export function toHex(rgb) {
    return "#" + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("");
}
function channel(c) {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
export function luminance([r, g, b]) {
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG contrast ratio, ≥ 1. */
export function contrast(a, b) {
    const la = luminance(a);
    const lb = luminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
function rgbToHsl([r, g, b]) {
    const rr = r / 255, gg = g / 255, bb = b / 255;
    const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    if (max === min)
        return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rr)
        h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
    else if (max === gg)
        h = ((bb - rr) / d + 2) / 6;
    else
        h = ((rr - gg) / d + 4) / 6;
    return [h, s, l];
}
function hslToRgb([h, s, l]) {
    if (s === 0)
        return [l * 255, l * 255, l * 255];
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
        if (t < 0)
            t += 1;
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return [hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255];
}
const WHITE = [255, 255, 255];
/**
 * The done-state tint when the tenant declares no `accent`: the platform success background,
 * PASSED THROUGH as a `var()` so `packages/design-tokens` stays the single source (and the
 * forms' "tokens, not literals" fence stays green). `applyBrandTokens`' `setProperty` accepts it.
 */
export const ACCENT_DEFAULT = "var(--feedback-success-bg)";
/** `amount` of `top` over `base` (0..1). */
export function mix(top, base, amount) {
    return [0, 1, 2].map((i) => top[i] * amount + base[i] * (1 - amount));
}
/** Darken (keeping hue and saturation) until BOTH white-on-it and it-on-surface clear the ratio. */
export function textSafe(colour, surface, ratio = 4.5) {
    const [h, s, l0] = rgbToHsl(colour);
    let l = l0;
    let candidate = colour;
    for (let i = 0; i < 40; i += 1) {
        if (contrast(WHITE, candidate) >= ratio && contrast(candidate, surface) >= ratio)
            return candidate;
        l = Math.max(0, l - 0.02);
        candidate = hslToRgb([h, s, l]);
    }
    return candidate;
}
/** Move away from `surface` (darker on a light surface) until the ratio clears. */
function ensureContrast(colour, surface, ratio) {
    const [h, s, l0] = rgbToHsl(colour);
    const darkerIsAway = luminance(surface) > 0.5;
    let l = l0;
    let candidate = colour;
    for (let i = 0; i < 40; i += 1) {
        if (contrast(candidate, surface) >= ratio)
            return candidate;
        l = darkerIsAway ? Math.max(0, l - 0.02) : Math.min(1, l + 0.02);
        candidate = hslToRgb([h, s, l]);
    }
    return candidate;
}
function deriveBase(input) {
    const primary = parseHex(input.primary);
    const secondary = parseHex(input.secondary) ?? primary;
    if (!primary || !secondary)
        return null;
    const [ph, ps] = rgbToHsl(primary);
    // Neutrals biased toward the primary hue: a light ground and a card one step lighter.
    const page = parseHex(input.surface_page ?? "") ?? mix(primary, WHITE, 0.06);
    const card = parseHex(input.surface_card ?? "") ?? mix(primary, WHITE, 0.02);
    // Text: near-black and mid-dark with the same hue, then forced past AA on the card.
    const textPrimary = ensureContrast(parseHex(input.text_primary ?? "") ?? hslToRgb([ph, Math.min(ps, 0.45), 0.13]), card, 7);
    const textSecondary = ensureContrast(parseHex(input.text_secondary ?? "") ?? hslToRgb([ph, Math.min(ps, 0.2), 0.37]), card, 4.5);
    return { primary, secondary, page, card, textPrimary, textSecondary };
}
/** `colour` at `alpha`, spelled as CSS. The one place a derived translucent colour is written. */
function rgba([r, g, b], alpha) {
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}
export function deriveBrandTokens(input) {
    const base = deriveBase(input);
    if (!base)
        return null;
    const { primary, secondary, page, card, textPrimary, textSecondary } = base;
    const primaryText = textSafe(primary, card);
    const secondaryText = textSafe(secondary, card);
    // Tenant override FIRST: an `accent` that parses as hex wins, unchanged. Otherwise the success tint.
    const accentOverride = parseHex(input.accent ?? "");
    const accent = accentOverride ? toHex(accentOverride) : ACCENT_DEFAULT;
    return {
        "--brand-primary": toHex(primary),
        "--brand-primary-text": toHex(primaryText),
        "--brand-secondary": toHex(secondary),
        "--brand-secondary-text": toHex(secondaryText),
        "--brand-accent": accent,
        "--surface-page": toHex(page),
        "--surface-card": toHex(card),
        "--text-primary": toHex(textPrimary),
        "--text-secondary": toHex(textSecondary),
        "--border": rgba(textSecondary, 0.32),
    };
}
export function applyBrandTokens(root, tokens) {
    for (const [name, value] of Object.entries(tokens))
        root.style.setProperty(name, value);
}
/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE CLIENT PORTAL'S MAPPING (#2663). A single-tenant client's portal wears their firm's brand:
   the side nav in the primary; the active item, buttons, links and focus in the secondary;
   grounds and text from the derivation above. The portal's stylesheets are untouched: they keep
   reading the `--ard-*` / `--btn-*` tokens, and these values are set over the token file's on
   <html>, where the token file's own aliases resolve.
   ───────────────────────────────────────────────────────────────────────────────────────────── */
/**
 * The opacities the portal's nav paints its TEXT at, over the nav ground (`portal.css`: 62% for
 * the sign-out, the "Viewing" label and the Powered-by line; 78% for an inactive item). The nav
 * is derived until the LOWEST of them clears AA, and the portal's nav fence holds the stylesheet
 * to exactly these.
 */
export const NAV_TEXT_ALPHAS = [0.62, 0.78];
/** Whole 8-bit channels: what a hex value paints, so every contrast check measures that. */
function snap(c) {
    return c.map((v) => Math.max(0, Math.min(255, Math.round(v))));
}
/**
 * Step `colour`'s lightness (hue and saturation kept) darker or lighter, 0.02 at a time, until
 * `ok` holds. `steps` is how far it moved (0: the colour was already fine and comes back as is);
 * when nothing clears, it ends at black or white and `steps` is infinite.
 */
function stepUntil(colour, darker, ok) {
    const [h, s, l0] = rgbToHsl(colour);
    let candidate = snap(colour);
    for (let i = 0; i <= 50; i += 1) {
        if (i > 0)
            candidate = snap(hslToRgb([h, s, darker ? Math.max(0, l0 - 0.02 * i) : Math.min(1, l0 + 0.02 * i)]));
        if (ok(candidate))
            return { colour: candidate, steps: i };
    }
    return { colour: candidate, steps: Number.POSITIVE_INFINITY };
}
/** `colour` `by` lighter (positive) or darker (negative) in HSL lightness: a hover or pressed step. */
function shift(colour, by) {
    const [h, s, l] = rgbToHsl(colour);
    return snap(hslToRgb([h, s, Math.max(0, Math.min(1, l + by))]));
}
/**
 * The portal's tokens for a tenant's brand inputs, or null when the primary does not parse (the
 * portal then stays AR Dept.). The map, and the bar each value is held to:
 *
 *   grounds  --ard-bg = the shared page; --ard-bg-card / --ard-surface = the shared card;
 *            --ard-surface-2 (hover rows, chips, the ghost hover) and --ard-surface-3 (the
 *            stepper's pending bar) step the page 6% / 14% toward the shared text-primary: the
 *            token file's bone → surface-2 → surface-3 ladder, in the firm's hue
 *   text     --ard-text (shared text-primary), --ard-text-2 and --ard-slate (shared
 *            text-secondary), --ard-muted (a lighter step of it): each darkened until it clears
 *            4.5:1 on every ground text sits on (page, card, surface-2)
 *   ink      --ard-ink-text (headings) and --ard-ink-fill (table heads): the primary, darkened
 *            until it clears 4.5:1 as text on those grounds AND under --ard-on-ink (its fills);
 *            --ard-on-ink and --ard-inverse are the card. The legacy --ard-ink is not mapped: since
 *            the v20 split the portal reads only the split tokens
 *   accent   --ard-coral, --btn-ink-default and --ard-border-focus: the secondary, darkened to the
 *            same two bars, so buttons, links and focus take the firm's second colour;
 *            --ard-coral-dark / --btn-ink-hover one hover step darker, --btn-ink-pressed one more;
 *            the coral button (--btn-coral-*) takes the same three steps
 *   edges    --ard-border = the shared border; --ard-border-strong (dividers, the ghost button)
 *            and --ard-border-control (inputs, checkboxes) darkened until it clears 3:1 on every
 *            ground
 *   literals --ring-focus, --ard-overlay and --btn-ghost-hover are colour LITERALS in the token
 *            file, so no alias would follow the brand: each is restated in the token file's own
 *            shape (ring geometry and alpha, overlay alpha), coloured from the brand
 *   nav      --portal-nav-fg is the derived light (--ard-inverse, lightened) or dark (--ard-text,
 *            darkened), each pushed only as far as the 62% blend over the primary needs;
 *            --portal-nav-bg is the primary, moved (away from the text) only when even white or
 *            black text would miss — never for a dark navy. Whichever text leaves the ground
 *            nearer the primary wins. --portal-nav-marker is the secondary, moved away from the
 *            nav ground until 3:1
 *
 * The tenant's `accent` (the forms' done-state tint) has no portal role: the portal's done and
 * error states are the platform's semantic tokens.
 */
export function derivePortalTokens(input) {
    const base = deriveBase(input);
    if (!base)
        return null;
    const [ph, ps] = rgbToHsl(base.primary);
    const primary = snap(base.primary);
    const page = snap(base.page);
    const card = snap(base.card);
    const surface2 = snap(mix(base.textPrimary, page, 0.06));
    const surface3 = snap(mix(base.textPrimary, page, 0.14));
    const grounds = [page, card, surface2];
    const onEveryGround = (ratio) => (c) => grounds.every((g) => contrast(c, g) >= ratio);
    // The grounds are light, so every text colour is found by darkening.
    const text = stepUntil(base.textPrimary, true, onEveryGround(4.5)).colour;
    const text2 = stepUntil(base.textSecondary, true, onEveryGround(4.5)).colour;
    const muted = stepUntil(hslToRgb([ph, Math.min(ps, 0.2), 0.45]), true, onEveryGround(4.5)).colour;
    const edge = stepUntil(hslToRgb([ph, Math.min(ps, 0.2), 0.6]), true, onEveryGround(3)).colour;
    const inverse = card;
    const textAndFill = (c) => onEveryGround(4.5)(c) && contrast(inverse, c) >= 4.5;
    const ink = stepUntil(base.primary, true, textAndFill).colour;
    const accent = stepUntil(base.secondary, true, textAndFill).colour;
    // The nav. A text candidate first moves toward its extreme (white / black) until its faintest
    // blend reads on the PRIMARY; only when the extreme still misses does the ground move, away
    // from it. Keep whichever candidate leaves the ground nearer the primary (a tie: the stronger).
    const faintest = Math.min(...NAV_TEXT_ALPHAS);
    const reads = (fg, bg) => contrast(mix(fg, bg, faintest), bg) >= 4.5;
    const navFor = (start, lightText) => {
        const fg = stepUntil(start, !lightText, (c) => reads(c, primary));
        const ground = Number.isFinite(fg.steps)
            ? { colour: primary, steps: 0 }
            : stepUntil(primary, lightText, (bg) => reads(fg.colour, bg));
        return { fg: fg.colour, lightText, ground };
    };
    const light = navFor(inverse, true);
    const dark = navFor(text, false);
    const nav = light.ground.steps < dark.ground.steps ||
        (light.ground.steps === dark.ground.steps && contrast(light.fg, primary) >= contrast(dark.fg, primary))
        ? light
        : dark;
    // A dark ground (light text) sends the marker lighter; a light one sends it darker.
    const marker = stepUntil(base.secondary, !nav.lightText, (c) => contrast(c, nav.ground.colour) >= 3).colour;
    return {
        "--portal-nav-bg": toHex(nav.ground.colour),
        "--portal-nav-fg": toHex(nav.fg),
        "--portal-nav-marker": toHex(marker),
        "--ard-bg": toHex(page),
        "--ard-bg-card": toHex(card),
        "--ard-surface": toHex(card),
        "--ard-surface-2": toHex(surface2),
        "--ard-surface-3": toHex(surface3),
        "--ard-text": toHex(text),
        "--ard-text-2": toHex(text2),
        "--ard-muted": toHex(muted),
        "--ard-slate": toHex(text2),
        "--ard-ink-fill": toHex(ink),
        "--ard-ink-text": toHex(ink),
        "--ard-on-ink": toHex(inverse),
        "--ard-inverse": toHex(inverse),
        "--ard-coral": toHex(accent),
        "--ard-coral-dark": toHex(shift(accent, -0.06)),
        "--ard-border": rgba(base.textSecondary, 0.32),
        "--ard-border-strong": toHex(edge),
        "--ard-border-control": toHex(edge),
        "--ard-border-focus": toHex(accent),
        "--ring-focus": `0 0 0 3px ${rgba(accent, 0.25)}`,
        "--btn-ink-default": toHex(accent),
        "--btn-ink-hover": toHex(shift(accent, -0.06)),
        "--btn-ink-pressed": toHex(shift(accent, -0.1)),
        "--btn-coral-default": toHex(accent),
        "--btn-coral-hover": toHex(shift(accent, -0.06)),
        "--btn-coral-pressed": toHex(shift(accent, -0.1)),
        "--btn-ghost-hover": toHex(surface2),
        "--ard-overlay": rgba(ink, 0.55),
    };
}
//# sourceMappingURL=brandTokens.js.map